/**
 * Date-format auto-detection and override parsing for date-bearing
 * `date_range`/`month`/`half_month` columns.
 *
 * Auto-detection tries, in order, `DD.MM.YYYY`, `YYYY-MM-DD`, and Excel
 * serial-number values against a column's sample values. An explicit
 * `AvailabilityProfile.dateFormatOverrides` entry (a preset constant OR an
 * arbitrary operator-typed pattern using the `yyyy`/`yy`/`MM`/`dd` tokens)
 * takes precedence over auto-detection when set. Pure — no DB/R2/Redis.
 *
 * Traceability: availability-profile-resolution / Requirement: Date-format
 * override for date-bearing columns.
 */

import { pad } from './header-match.js';

export const DATE_FORMAT_DD_MM_YYYY = 'DD.MM.YYYY';
export const DATE_FORMAT_YYYY_MM_DD = 'YYYY-MM-DD';
export const DATE_FORMAT_EXCEL_SERIAL = 'EXCEL_SERIAL';

const ISO_RE = /^\d{4}-\d{2}-\d{2}$/;
const DMY_RE = /^\d{1,2}[./-]\d{1,2}[./-]\d{4}$/;
const EXCEL_SERIAL_RE = /^\d{4,6}$/;

/** Standard Excel/XLSX epoch (1899-12-30), the widely-used 1900-date-system convention. */
const EXCEL_EPOCH_UTC_MS = Date.UTC(1899, 11, 30);
const MS_PER_DAY = 86_400_000;

/**
 * Confirms `(year, month, day)` is a real calendar date, not just each
 * field independently in-range. JS `Date` silently normalizes an
 * out-of-range day/month combo (e.g. April 31 rolls forward to May 1), so
 * round-tripping through `Date` and comparing the normalized month/day back
 * against the input is how that rollover is detected and rejected.
 */
function isValidCalendarDate(year: number, month: number, day: number): boolean {
  const date = new Date(year, month - 1, day);
  return date.getMonth() + 1 === month && date.getDate() === day;
}

function nonBlank(values: string[]): string[] {
  return values.map((v) => v.trim()).filter((v) => v !== '');
}

/**
 * Auto-detects a date-bearing column's format by trying, in order,
 * `DD.MM.YYYY`, `YYYY-MM-DD`, and Excel serial-number shapes against every
 * non-blank sample value. Returns `null` when no single format matches
 * every sample (or there are no non-blank samples at all).
 */
export function detectDateFormat(sampleValues: string[]): string | null {
  const samples = nonBlank(sampleValues);
  if (samples.length === 0) return null;

  if (samples.every((v) => ISO_RE.test(v))) return DATE_FORMAT_YYYY_MM_DD;
  if (samples.every((v) => DMY_RE.test(v))) return DATE_FORMAT_DD_MM_YYYY;
  if (samples.every((v) => EXCEL_SERIAL_RE.test(v))) return DATE_FORMAT_EXCEL_SERIAL;

  return null;
}

function excelSerialToIso(serial: number): string | null {
  if (!Number.isFinite(serial)) return null;
  const ms = EXCEL_EPOCH_UTC_MS + serial * MS_PER_DAY;
  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) return null;
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
}

type DateToken = 'yyyy' | 'yy' | 'MM' | 'dd';
const TOKEN_RE = /(yyyy|yy|MM|dd)/g;

function escapeRegExp(literal: string): string {
  return literal.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Builds a matching regex + token order from an arbitrary pattern string
 * (e.g. `dd.MM.yyyy`, `MM/dd/yy`) using the `yyyy`/`yy`/`MM`/`dd` tokens,
 * with any other characters treated as literal separators.
 */
function compilePattern(format: string): { regex: RegExp; tokens: DateToken[] } {
  const tokens: DateToken[] = [];
  let regexSource = '';
  let cursor = 0;
  let match: RegExpExecArray | null;

  TOKEN_RE.lastIndex = 0;
  while ((match = TOKEN_RE.exec(format)) !== null) {
    regexSource += escapeRegExp(format.slice(cursor, match.index));
    const token = match[0] as DateToken;
    tokens.push(token);
    regexSource += token === 'yyyy' ? '(\\d{4})' : '(\\d{2})';
    cursor = TOKEN_RE.lastIndex;
  }
  regexSource += escapeRegExp(format.slice(cursor));

  return { regex: new RegExp(`^${regexSource}$`), tokens };
}

function parseWithPattern(raw: string, format: string): string | null {
  const { regex, tokens } = compilePattern(format);
  const match = raw.trim().match(regex);
  if (!match) return null;

  let year: number | null = null;
  let month: number | null = null;
  let day: number | null = null;

  tokens.forEach((token, i) => {
    const value = Number(match[i + 1]);
    if (token === 'yyyy') year = value;
    else if (token === 'yy') year = 2000 + value;
    else if (token === 'MM') month = value;
    else if (token === 'dd') day = value;
  });

  if (year === null || month === null || day === null) return null;
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  if (!isValidCalendarDate(year, month, day)) return null;

  return `${year}-${pad(month)}-${pad(day)}`;
}

function parseIso(trimmed: string): string | null {
  return ISO_RE.test(trimmed) ? trimmed : null;
}

/** Matched directly (not via compilePattern) so any of `.`/`-`/`/` is accepted as the separator. */
function parseDmy(trimmed: string): string | null {
  const match = trimmed.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
  if (!match) return null;
  const [, day, month, year] = match;
  const dayNum = Number(day);
  const monthNum = Number(month);
  const yearNum = Number(year);
  if (monthNum < 1 || monthNum > 12 || dayNum < 1 || dayNum > 31) return null;
  if (!isValidCalendarDate(yearNum, monthNum, dayNum)) return null;
  return `${year}-${pad(monthNum)}-${pad(dayNum)}`;
}

function parseExcelSerial(trimmed: string): string | null {
  return EXCEL_SERIAL_RE.test(trimmed) ? excelSerialToIso(Number(trimmed)) : null;
}

const PRESET_PARSERS: Record<string, (trimmed: string) => string | null> = {
  [DATE_FORMAT_YYYY_MM_DD]: parseIso,
  [DATE_FORMAT_DD_MM_YYYY]: parseDmy,
  [DATE_FORMAT_EXCEL_SERIAL]: parseExcelSerial,
};

/**
 * Parses a raw cell value into an ISO `YYYY-MM-DD` date string using an
 * explicit format — one of the 3 preset constants, or an arbitrary
 * operator-typed pattern built from `yyyy`/`yy`/`MM`/`dd` tokens (e.g.
 * `dd-MM-yy`). Returns `null` when the value does not match the format.
 */
export function parseDateWithFormat(raw: string, format: string): string | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;

  const presetParser = PRESET_PARSERS[format];
  if (presetParser) return presetParser(trimmed);

  return parseWithPattern(trimmed, format);
}

/**
 * Parses a raw cell value into an ISO `YYYY-MM-DD` date string with no
 * explicit `dateFormatOverrides` entry: tries already-ISO values, then the
 * common Polish `DD.MM.YYYY` (or `DD-MM-YYYY`/`DD/MM/YYYY`) supplier
 * format. Both paths validate the parsed values are a real calendar date
 * (`isValidCalendarDate`), so a calendar-invalid date like `45.99.2026`
 * returns `null` rather than a bogus normalized string. Returns `null` when
 * the value does not match either shape.
 *
 * The single default (no-override) date parser for `date_range` columns —
 * shared by the api/worker's write-path row interpretation
 * (`packages/db/src/queries/availability-windows/interpret-row.ts`) and the
 * web preview's client-side row derivation
 * (`packages/screens/src/availability-import/lib/derive-interpreted-rows.ts`),
 * so the period an operator sees in preview never diverges from what the
 * worker actually writes.
 */
export function parseFlexibleDate(raw: string): string | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  return parseIso(trimmed) ?? parseDmy(trimmed);
}

/**
 * Parses one `date_range` column's raw cell value for a given row, using
 * the profile's `dateFormatOverrides` entry for that column when set
 * (operator-provided override always wins), otherwise `parseFlexibleDate`.
 *
 * Lives here (not duplicated per-consumer) so the write path
 * (`packages/db/src/queries/availability-windows/interpret-row.ts`) and the
 * web preview's client-side row derivation
 * (`packages/screens/src/availability-import/lib/derive-interpreted-rows.ts`)
 * share one implementation — a future change to override handling can't
 * silently diverge between what the operator previews and what the worker
 * writes.
 */
export function parseDateRangeCell(
  dateFormatOverrides: Record<string, string> | null | undefined,
  raw: string,
  column: string,
): string | null {
  const override = dateFormatOverrides?.[column];
  if (override !== undefined) return parseDateWithFormat(raw, override);
  return parseFlexibleDate(raw);
}
